import React, { useState } from "react";
import axios from "axios";
import { Link, useHistory } from "react-router-dom/cjs/react-router-dom";
import { path } from "../../path";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";

const CreateJudgeApplication = () => {
  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    upi: "",
    skill: "",
    subSkill: "",
    yearsOfExperience: "",
    achievements: "",
    workLinks: [],
    cameraMicAvailable: true,
    allowPromotion: true,
    subscribeUpdates: true
  };
  const [judgeData, setJudgeData] = useState(initialValues);
  const [intro, setIntro] = useState();
  const [signatureVideos, setSignatureVideos] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingPercent, setUploadingPercentage] = useState(0);
  const history = useHistory();

  const handleChange = (ev) => {
    if (ev.target.name === "workLinks") {
      setJudgeData((prev) => ({
        ...prev,
        workLinks: ev.target.value.split(","),
      }));
      return;
    }
    setJudgeData((prev) => ({ ...prev, [ev.target.name]: ev.target.value }));
  };

  const handleIntro = (ev) => {
    setIntro(ev.target.files[0]);
  };

  const handleMultipleFiles = (ev) => {
    const files = Array.from(ev.target.files); // Convert FileList to an array
    if (ev.target.id === "signatureVideos") {
      setSignatureVideos([...files]);
    } else if (ev.target.id === "qualifications") {
      setQualifications([...files]);
    }
  };

  const generateFileName = (file, mediaType) => {
    const timestamp = Date.now();
    const ext = mediaType === "image" ? ".jpeg" : ".mp4";

    // Remove existing extension and non-word characters
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, ""); // strips `.xxx`
    const safeBaseName = nameWithoutExt.replace(/\W+/g, ""); // strips special chars

    return `Box_${timestamp}_${safeBaseName}${ext}`;
  };

  const MAX_SIZE = 50 * 1024 * 1024; // 50MB

  const uploadFileToBox = async (file, type, accessToken, onProgress = () => {}) => {
    if (!file) {
      throw new Error("No file provided");
    }
    if (!accessToken) {
      throw new Error("Access token not provided");
    }

    const fileName = generateFileName(file, type);

    if (file.size < MAX_SIZE) {
      return await directUpload(file, accessToken, fileName, onProgress);
    } else {
      return await chunkedUpload(file, accessToken, fileName, onProgress);
    }
  };

  const directUpload = async (file, accessToken, fileName, onProgress) => {
    const formData = new FormData();
    formData.append("file", file, fileName);
    formData.append("parent_id", "0");

    const response = await axios.post(
      "https://upload.box.com/api/2.0/files/content",
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          onProgress(percent);
        },
      }
    );

    const fileData = response.data.entries[0];

    const { data: urlData } = await axios.post(`${path}/genrate-Url`, {
      id: fileData.id,
      name: fileName,
    });

    return {
      file_id: fileData.id,
      file_url: urlData.file_url,
    };
  };

  const chunkedUpload = async (file, accessToken, fileName, onProgress) => {
    const { data: session } = await axios.post(
      "https://upload.box.com/api/2.0/files/upload_sessions",
      {
        folder_id: "0",
        file_size: file.size,
        file_name: fileName,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    const { id: sessionId, part_size: partSize } = session;
    let offset = 0;
    const parts = [];
    let shaTotal = CryptoJS.algo.SHA1.create();

    while (offset < file.size) {
      const chunk = file.slice(offset, offset + partSize);
      const chunkBuffer = await chunk.arrayBuffer();
      const wordArray = CryptoJS.lib.WordArray.create(chunkBuffer);
      const sha1Digest = CryptoJS.SHA1(wordArray).toString(CryptoJS.enc.Base64);

      shaTotal.update(wordArray);

      const { data } = await axios.put(
        `https://upload.box.com/api/2.0/files/upload_sessions/${sessionId}`,
        new Blob([chunkBuffer]),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/octet-stream",
            "Content-Range": `bytes ${offset}-${offset + chunkBuffer.byteLength - 1}/${file.size}`,
            Digest: `sha=${sha1Digest}`,
          },
          onUploadProgress: (e) => {
            const percent = (((offset + e.loaded) / file.size) * 100).toFixed(
              2
            );
            onProgress(percent);
          },
        }
      );

      parts.push(data.part);
      offset += chunkBuffer.byteLength;
    }

    const fullDigest = shaTotal.finalize().toString(CryptoJS.enc.Base64);

    const commitRes = await axios.post(
      `https://upload.box.com/api/2.0/files/upload_sessions/${sessionId}/commit`,
      { parts },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Digest: `sha=${fullDigest}`,
          "Content-Type": "application/json",
        },
      }
    );

    const fileData = commitRes.data.entries[0];

    // Generate file URL (adjust as per your API)
    const { data: urlData } = await axios.post(`${path}/genrate-Url`, {
      id: fileData.id,
      name: fileName,
    });

    return {
      file_id: fileData.id,
      file_url: urlData.file_url,
    };
  };

  const getAccessToken = async () => {
    const { data } = await axios.get(`${path}/getUploadToken`);
    return data.accessToken;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setLoading(true);

    try {
      if (
        !judgeData ||
        Object.values(judgeData).every((value) => value === "")
      ) {
        console.error("Error: Judge data cannot be empty");
        toast.error("Error: Judge data cannot be empty");
        setLoading(false);
        return;
      }

      // Validate required fields
      const requiredFields = [
        "firstName",
        "lastName",
        "email",
        "contactNumber",
        "upi",
        "skill",
        "subSkill",
        "yearsOfExperience",
        "achievements",
        "cameraMicAvailable",
        "allowPromotion",
        "subscribeUpdates"
      ];
      for (const field of requiredFields) {
        if (!judgeData[field]) {
          console.error(`Error: ${field} is required`);
          toast.error(`Error: ${field} is required`);
          setLoading(false);
          return;
        }
      }

      // check user exists
       let user;
        try {
          const response = await axios.post(`${path}/checkUserExists`, {
            email: judgeData?.email,
          });

          user = response?.data?.user;
        } catch (err) {
          toast.error("User with this email does not exist");
          setLoading(false);
          return;
        }

        if (!user) {
          toast.error("User with this email does not exist");
          setLoading(false);
          return;
        }
      
      const accessToken = await getAccessToken();
      const filesToUpload = [];

      if (intro) {
        filesToUpload.push({ label: "intro", file: intro, type: "video" });
      }

      (signatureVideos || []).forEach((file, i) =>
        filesToUpload.push({ label: `performance-${i}`, file, type: "video" })
      );

      (qualifications || []).forEach((file, i) =>
        filesToUpload.push({ label: `certification-${i}`, file, type: "image" })
      );

      const totalSize = filesToUpload.reduce((sum, f) => sum + f.file.size, 0);
      const uploadedMap = {}; // Track current uploaded bytes for each file

      const updateProgress = () => {
        const uploaded = Object.values(uploadedMap).reduce((a, b) => a + b, 0);
        const percent = Math.min(Math.round((uploaded / totalSize) * 100), 100);
        setUploadingPercentage(percent);
      };

      const uploadPromises = filesToUpload.map(({ label, file, type }) => {
        return uploadFileToBox(file, type, accessToken, (percent) => {
          uploadedMap[label] = (file.size * percent) / 100;
          updateProgress();
        }).then((result) => ({ label, result }));
      });

      const uploadedResults = await Promise.all(uploadPromises);

      const payload = {
        userId: user?._id,
        firstName: judgeData.firstName,
        lastName: judgeData.lastName,
        email: judgeData.email,
        contactNumber: judgeData.contactNumber,
        upi: judgeData.upi,
        yearsOfExp: judgeData.yearsOfExperience,
        skill: judgeData.skill,
        subCat: judgeData.subSkill,
        achievements: judgeData.achievements,
        workLinks: judgeData.workLinks,
        profilePicture: null,
        intro: null,
        performances: [],
        certifications: [],
        cameraMicAvailable: judgeData.cameraMicAvailable,
        allowPromotion: judgeData.allowPromotion,
        subscribeUpdates: judgeData.subscribeUpdates,
      };

      uploadedResults.forEach(({ label, result }) => {
        if (label === "intro") {
          payload.intro = result;
        } else if (label.startsWith("performance")) {
          payload.performances.push(result);
        } else if (label.startsWith("certification")) {
          payload.certifications.push(result);
        }
      });

      setUploadingPercentage(100);
      console.info("Final Payload:", payload);

      const response = await axios.post(`${path}/v2/newJudgeData`, payload);

      console.info("judge response ", response.data);

      history.goBack()

      console.log("Success:", response.data);
      toast.success("Success: "+response.data.message);
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      toast.error(error.response?.data?.error || error?.message || "There was a error processing file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-col px-[20%] justify-center  bg-white">
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50 text-white text-lg font-semibold h-screen w-screen">
          {uploadingPercent > 0 &&
            uploadingPercent < 99 &&
            `⏳ Uploading ${uploadingPercent}%`}
          {uploadingPercent === 0 &&
            "Processing.."}
        </div>
      )}
      <div className="flex w-full fixed items-center  bg-white mb-4 h-20 space-x-5 overflow-hidden ">
        <Link to={"/marketingAdmin"}>
          <div className="flex items-center ">
            <img
              src="https://w7.pngwing.com/pngs/848/762/png-transparent-computer-icons-home-house-home-angle-building-rectangle-thumbnail.png"
              className="h-6 mr-4"
              alt="home"
            />
            <h3>Home</h3>
          </div>
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="space-y-2 py-20">
        <h1 className="text-xl font-bold  mb-4 text-center">
          Create New Application
        </h1>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            FirstName*
          </label>
          <input
            type="text"
            name="firstName"
            value={judgeData.firstName}
            onChange={handleChange}
            className=" block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            LastName*
          </label>
          <input
            type="text"
            name="lastName"
            value={judgeData.lastName}
            onChange={handleChange}
            className=" block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email*
          </label>
          <input
            type="text"
            name="email"
            value={judgeData.email}
            onChange={handleChange}
            className=" block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Contact Number*
          </label>
          <input
            type="number"
            name="contactNumber"
            value={judgeData.contactNumber}
            onChange={handleChange}
            className=" block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Upi*</label>
          <input
            type="text"
            name="upi"
            value={judgeData.upi}
            onChange={handleChange}
            className=" block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Skill Category* (eg: Musical Instrument, Dancing, etc.)
          </label>
          <input
            type="text"
            name="skill"
            value={judgeData.skill}
            onChange={handleChange}
            className=" block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Sub Skill-Category* (eg: Guitar, Sitar, etc.)
          </label>
          <input
            type="text"
            name="subSkill"
            value={judgeData.subSkill}
            onChange={handleChange}
            className=" block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Years Of Experience*
          </label>
          <input
            type="number"
            name="yearsOfExperience"
            value={judgeData.yearsOfExperience}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Acheivements*
          </label>
          <textarea
            type="text"
            name="achievements"
            rows={4}
            value={judgeData.achievements}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Work Links
          </label>
          <input
            type="text"
            name="workLinks"
            value={judgeData.workLinks.join(",")}
            onChange={handleChange}
            placeholder="https:abc.com,https:sample.com"
            className="mt-1 block w-full rounded-md border border-blue-800 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          />
        </div>
        <div className="">
          <label
            className="block text-gray-700 font-medium mb-2"
            htmlFor="file">
            Intro Video*
          </label>
          <input
            type="file"
            id="file"
            accept="video/*"
            onChange={handleIntro}
            className="w-full px-4 py-2 border rounded-md focus:outline-none"
          />
        </div>
        <div>
          <label
            className="block text-gray-700 font-medium mb-2"
            htmlFor="file">
            Signature Videos*
          </label>
          <input
            type="file"
            id="signatureVideos"
            accept="video/*"
            multiple // Allows multiple file selection
            onChange={handleMultipleFiles}
            className="w-full px-4 py-2 border rounded-md focus:outline-none"
          />
        </div>
        <div>
          <label
            className="block text-gray-700 font-medium mb-2"
            htmlFor="file">
            Qualifications* (Only images allowed)
          </label>
          <input
            type="file"
            id="qualifications"
            accept="image/png, image/jpeg, image/jpg"
            multiple // Allows multiple file selection
            onChange={handleMultipleFiles}
            className="w-full px-4 py-2 border rounded-md focus:outline-none"
          />
        </div>
        <div className="flex flex-row space-x-2">
          <input
            id="cameraMicAvailable"
            type="checkbox"
            checked={judgeData.cameraMicAvailable}
            onChange={(e) =>
              setJudgeData((prev) => ({ ...prev, cameraMicAvailable: e.target.checked }))
            }
            className="accent-teal-600"
          />
          <label htmlFor="cameraMicAvailable" className="flex items-start gap-2 font-medium text-gray-700">
            {`Camera & mic available for promotional content.`}
          </label>
        </div>
        <div className="flex flex-row space-x-2">
          <input
            id="allowPromotion"
            type="checkbox"
            checked={judgeData.allowPromotion}
            onChange={(e) =>
              setJudgeData((prev) => ({ ...prev, allowPromotion: e.target.checked }))
            }
            className="accent-teal-600"
          />
          <label htmlFor="allowPromotion" className="flex items-start gap-2 font-medium text-gray-700">
            {`I'd like the Feedants team to create/promote an ad featuring me.`}
          </label>
        </div>
        <div className="flex flex-row space-x-2">
          <input
            id="subscribeUpdates"
            type="checkbox"
            checked={judgeData.subscribeUpdates}
            onChange={(e) =>
              setJudgeData((prev) => ({ ...prev, subscribeUpdates: e.target.checked }))
            }
            className="accent-teal-600"
          />
          <label htmlFor="subscribeUpdates" className="flex items-start gap-2 font-medium text-gray-700">
            {`I'd like to subscribe to Judge-only updates & tips.`}
          </label>
        </div>
        <button
          type="submit"
          className="bg-teal-500 text-white px-4 py-2 rounded-md">
          Create
        </button>
      </form>
    </div>
  );
};

export default CreateJudgeApplication;
